/**
 * useDietPlanAnalytics.js
 *
 * Analytics hook for the Diet Plan Screen. Mirrors useSummaryAnalytics.js
 * exactly — same GA4 wrapper via react-ga4, same IS_PRODUCTION guard,
 * same dev console.group/table logging.
 *
 * Per FSD v2.1 DEC-22 / Policy §19: only funnel/product events, no raw
 * health answers. `risk_flag` is the one derived boolean this screen
 * sends — it's the stored decision, not the underlying medical data, so
 * it's fine to include. Nothing here sends calories, macros, conditions,
 * or free-text health info.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[DietPlanAnalytics] ${eventName}`, "color:#2E7D32;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useDietPlanAnalytics = () => {
  const trackStepView = useCallback((sessionId, riskFlag) => {
    send("diet_plan_view", { session_id: sessionId, risk_flag: riskFlag });
  }, []);

  const trackRiskAcknowledged = useCallback((sessionId) => {
    send("diet_plan_risk_acknowledged", { session_id: sessionId });
  }, []);

  const trackMealExpand = useCallback((sessionId, mealId, expanded) => {
    send("diet_plan_meal_expand", { session_id: sessionId, meal: mealId, expanded });
  }, []);

  const trackRegenerateClick = useCallback((sessionId, mealId) => {
    send("diet_plan_regenerate_click", { session_id: sessionId, meal: mealId });
  }, []);

  const trackRegenerateResult = useCallback((sessionId, mealId, success) => {
    send("diet_plan_regenerate_result", { session_id: sessionId, meal: mealId, success });
  }, []);

  // FSD v2.1 DEC-15: fires when the per-meal (3) or per-plan (10) cap
  // blocks a regenerate click, so product can see how often users hit it.
  const trackRegenerateLimitReached = useCallback((sessionId, mealId, limitType) => {
    send("diet_plan_regenerate_limit_reached", {
      session_id: sessionId,
      meal: mealId,
      limit_type: limitType, // "per_meal" | "per_plan"
    });
  }, []);

  const trackReplaceIngredientOpen = useCallback((sessionId, mealId, ingredientId) => {
    send("diet_plan_replace_ingredient_open", {
      session_id: sessionId,
      meal: mealId,
      ingredient: ingredientId,
    });
  }, []);

  const trackReplaceIngredientSelect = useCallback((sessionId, mealId, ingredientId, substitute) => {
    send("diet_plan_replace_ingredient_select", {
      session_id: sessionId,
      meal: mealId,
      ingredient: ingredientId,
      substitute,
    });
  }, []);

  // FSD v2.1 DEC-16: fires when a replace response reports it couldn't
  // safely auto-rebalance the day — a "warn instead of inventing food"
  // outcome worth tracking separately from a normal successful replace.
  const trackRebalanceWarning = useCallback((sessionId, mealId) => {
    send("diet_plan_rebalance_warning", { session_id: sessionId, meal: mealId });
  }, []);

  const trackDownloadPdf = useCallback((sessionId) => {
    send("diet_plan_download_pdf_click", { session_id: sessionId });
  }, []);

  const trackEmailPlanOpen = useCallback((sessionId) => {
    send("diet_plan_email_plan_open", { session_id: sessionId });
  }, []);

  const trackShare = useCallback((sessionId, method) => {
    send("diet_plan_share_click", { session_id: sessionId, method });
  }, []);

  return {
    trackStepView,
    trackRiskAcknowledged,
    trackMealExpand,
    trackRegenerateClick,
    trackRegenerateResult,
    trackRegenerateLimitReached,
    trackReplaceIngredientOpen,
    trackReplaceIngredientSelect,
    trackRebalanceWarning,
    trackDownloadPdf,
    trackEmailPlanOpen,
    trackShare,
  };
};

export default useDietPlanAnalytics;